// 빔(직선 지속 광선): 영웅 앞에서 앞으로 곧게 뻗어 life초 동안 유지되는 광선. 처음 0.07초에 끝까지 뻗고, 노이즈 UV 스크롤로 결이 흐르고,
// 폭이 느리게 숨 쉬며, 끝나기 직전 가늘어지며 사라진다. 시작(총구)과 끝(착탄)에 밝은 점이 있다.
// 가로·세로 띠 두 장을 십자로 둔다(투사체와 같은 이유: 어느 방향에서 봐도 보인다).
// 쓰는 칸: length = 길이(m), width = 굵기(m), y = 높이, ahead = 시작을 앞으로 옮기는 거리, life = 유지 시간(초),
//          color(몸통)·color2(핵)·intensity·seed. 드로우콜 1.
// Blender 메시로 바꿀 때: vfx-library/README.md "Blender로 바꿀 자리"(u = 시작 → 끝 0..1, v = 가로 0..1).
import * as THREE from 'three/webgpu';
import { abs, clamp, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01, easeOut } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const BEAM_SEG = 10;
/** 시작 부분이 가늘게 모이는 모양(총구): 앞 12%에서 35% → 100% */
const PINCH = new Float32Array(BEAM_SEG + 1);
for (let i = 0; i <= BEAM_SEG; i++) PINCH[i] = 0.35 + 0.65 * Math.min(1, i / BEAM_SEG / 0.12);

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const env = ctl.y, seed = ctl.z;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const body = band.oneMinus().pow(1.15);
  const core = band.oneMinus().pow(3.5);
  // 결: 시작 → 끝으로 흐른다. 두 겹을 다른 속도로
  const n1 = texture(noise, vec2(u.mul(3).sub(t2.w.mul(2.4)), v.mul(0.7).add(seed))).g;
  const n2 = texture(noise, vec2(u.mul(7).sub(t2.w.mul(4.1)), v.mul(1.3).add(seed.mul(2)))).r;
  // 총구·착탄 밝은 점
  const muzzle = smoothstep(0.1, 0.0, u);
  const impact = smoothstep(0.9, 1.0, u);
  const ends = muzzle.mul(0.7).add(impact.mul(0.55));
  const grain = n1.mul(0.55).add(n2.mul(0.3)).add(0.5);
  const alpha = clamp(body.mul(grain).add(core.mul(0.5)).add(ends.mul(body)), 0, 1).mul(env);
  const hot = clamp(core.mul(0.85).add(ends.mul(0.5)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(0.75);
  return fxMaterial(col, alpha);
}

export class BeamModule extends BatchModule {
  readonly kind: ModuleKind = 'beam';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.beam) {
    super(cap, stripLayout(BEAM_SEG, 2), makeMaterial(noise), 23, 'fx-beam');
  }

  /** 끝나기 직전 22% 구간의 진행(0..1) */
  private static release(s: Slot): number { return clamp01((s.age / s.life - 0.78) / 0.22); }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const L = Math.max(0.05, s.length * s.scale);
    // 처음 0.07초에 끝까지 뻗는다
    const reach = L * easeOut(clamp01(s.age / 0.07));
    const rel = BeamModule.release(s);
    // 폭: 천천히 숨 쉬고(약 4Hz, ±6%) 끝에서 가늘어진다
    const W = s.width * s.scale * (1 + 0.06 * Math.sin(s.age * 25 + s.seed * 6.283)) * (1 - 0.75 * rel * rel);
    const bx = s.x + sin * s.ahead, bz = s.z + cos * s.ahead;
    const rx = cos, rz = -sin;
    const per = (BEAM_SEG + 1) * 2;
    for (let i = 0; i <= BEAM_SEG; i++) {
      const d = (reach * i) / BEAM_SEG;
      const px = bx + sin * d, pz = bz + cos * d;
      const hw = 0.5 * W * PINCH[i];
      const k = o + i * 6;
      out[k] = px - rx * hw; out[k + 1] = s.y; out[k + 2] = pz - rz * hw;
      out[k + 3] = px + rx * hw; out[k + 4] = s.y; out[k + 5] = pz + rz * hw;
      const q = o + per * 3 + i * 6;
      out[q] = px; out[q + 1] = s.y - hw; out[q + 2] = pz;
      out[q + 3] = px; out[q + 4] = s.y + hw; out[q + 5] = pz;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.03) * (1 - BeamModule.release(s));
    out[2] = s.seed;
    out[3] = 0;
  }

  /** aTint2.w = 흐른 시간(초): UV 스크롤 */
  protected override tint2w(s: Slot): number { return s.age; }
}
